import styled from 'styled-components'
import breakpoints from '../../../../config/Breakpoints'
import CardTitle from '../../../../common/styles/CardTitle'
import useBookStatisticsCategory from '../hooks/useBookStatisticsCategory'
import StatisticsTableRow from '../../../../common/StatisticsTableRow'
import BooksitoutImages from '../../../../images/BooksitoutImages'
import LoadingBar from '../../../../common/LoadingBar'
import Spacer from '../../../../common/Spacer'
import booksitoutIcon from '../../../../config/BooksitoutIcon'
import {useNavigate} from 'react-router-dom'

const categoryKeyMap: Record<number, string> = {
	0: 'OTHERS',
	1: 'PHILOSOPHY',
	2: 'RELIGION',
	3: 'SOCIAL_SCIENCE',
	4: 'NATURAL_SCIENCE',
	5: 'TECHNOLOGY',
	6: 'ART',
	7: 'LANGUAGE',
	8: 'LITERATURE',
	9: 'HISTORY',
	10: 'UNKNOWN',
}

const BookStatisticsCategoryCard = () => {
	const navigate = useNavigate()
	const [categories, totalCount, isLoading] = useBookStatisticsCategory()

	const getPercent = (count: number | undefined, totalCount: number): string => {
		const validCount = count ?? 0
		const percentageRaw = totalCount > 0 ? (validCount / totalCount) * 100 : 0
		const precision = percentageRaw < 1 ? 1 : 0
		const percentage = percentageRaw.toFixed(precision)

		if (validCount === 0) {
			return ''
		}

		return `${percentage}%`
	}

	const rows: StatisticsTableRow[] = [
		{id: 0, icon: BooksitoutImages.StatisticsCategory.others, name: '총류', value: categories?.othersCount?.toString() ?? '0', extraValue: getPercent(categories?.othersCount, totalCount)},
		{id: 1, icon: BooksitoutImages.StatisticsCategory.philosophy, name: '철학', value: categories?.philosophyCount?.toString() ?? '0', extraValue: getPercent(categories?.philosophyCount, totalCount)},
		{id: 2, icon: BooksitoutImages.StatisticsCategory.religion, name: '종교', value: categories?.religionCount?.toString() ?? '0', extraValue: getPercent(categories?.religionCount, totalCount)},
		{id: 3, icon: BooksitoutImages.StatisticsCategory.socialScience, name: '사회과학', value: categories?.socialScienceCount?.toString() ?? '0', extraValue: getPercent(categories?.socialScienceCount, totalCount)},
		{id: 4, icon: BooksitoutImages.StatisticsCategory.naturalScience, name: '자연과학', value: categories?.naturalScienceCount?.toString() ?? '0', extraValue: getPercent(categories?.naturalScienceCount, totalCount)},
		{id: 5, icon: BooksitoutImages.StatisticsCategory.technology, name: '기술', value: categories?.technologyCount?.toString() ?? '0', extraValue: getPercent(categories?.technologyCount, totalCount)},
		{id: 6, icon: BooksitoutImages.StatisticsCategory.art, name: '예술', value: categories?.artCount?.toString() ?? '0', extraValue: getPercent(categories?.artCount, totalCount)},
		{id: 7, icon: BooksitoutImages.StatisticsCategory.language, name: '언어', value: categories?.languageCount?.toString() ?? '0', extraValue: getPercent(categories?.languageCount, totalCount)},
		{id: 8, icon: BooksitoutImages.StatisticsCategory.literature, name: '문학', value: categories?.literatureCount?.toString() ?? '0', extraValue: getPercent(categories?.literatureCount, totalCount)},
		{id: 9, icon: BooksitoutImages.StatisticsCategory.history, name: '역사', value: categories?.historyCount?.toString() ?? '0', extraValue: getPercent(categories?.historyCount, totalCount)},
		{id: 10, icon: BooksitoutImages.StatisticsCategory.question, name: '?', value: categories?.unknownCount?.toString() ?? '0', extraValue: getPercent(categories?.unknownCount, totalCount)},
	]

	return (
		<Container>
			<CardTitle icon={null} mb={0} title={'장르별 독서 요약'} url={''} textSize={5}/>

			<Table>
				<tbody>
				{rows.map(row => {
					const hasBooks = row.value !== '0'
					return (
						<RowLink key={row.id} onClick={hasBooks ? () => navigate(`/book/statistics/categories/${categoryKeyMap[row.id]}`) : undefined} $clickable={hasBooks}>
							<th className={'col-7 h5'} style={{color: 'var(--text-color)'}}>
								<Image src={row.icon} alt={''}/>
								{row.name}
							</th>
							<td className={'col-5'} style={{color: 'var(--text-color)'}}>
								<h5 className={'d-flex flex-row'} style={{margin: 0, height: '32px', alignItems: 'center'}}>
									{isLoading ? (
										<div style={{width: '100%'}}>
											<LoadingBar size={3}/>
										</div>
									) : (
										<>
											<CountValue>{row.value}</CountValue>
											{row.extraValue && (
												<ExtraValue>{row.extraValue}</ExtraValue>
											)}
											{hasBooks && (
												<>
													<Spacer/>
													<ChevronIcon>
														<booksitoutIcon.ArrowRight/>
													</ChevronIcon>
												</>
											)}
										</>
									)}
								</h5>
							</td>
						</RowLink>
					)
				})}
				</tbody>
			</Table>
		</Container>
	)
}

const Container = styled.div`
	height: 100%;
	min-height: 650px;
	background-color: var(--card-bg-color);
	border-radius: 16px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
	padding: 25px;

	@media (max-width: ${breakpoints.md}) {
		height: auto;
		min-height: 0;
	}
`

const Table = styled.table`
	margin: 0;
	width: 100%;

	tbody {
		background-color: transparent !important;
	}

	th, td {
		border-bottom: 1px solid var(--hover-bg-color);
		padding: 10px 8px;
	}
`

const Image = styled.img.attrs({
	className: 'img-fluid me-3 force-1-line',
})`
	width: 30px;
	height: 30px;
`

const CountValue = styled.span`
	min-width: 40px;
	text-align: right;
	margin-right: 5px;
`

const ExtraValue = styled.div`
	padding-top: 7px;
	margin: 0;
	font-size: 14px;
	color: var(--sub-text-color);
	min-width: 45px;
	text-align: right;
`

const RowLink = styled.tr<{ $clickable: boolean }>`
	cursor: ${({$clickable}) => $clickable ? 'pointer' : 'default'};

	&:hover {
		background-color: ${({$clickable}) => $clickable ? 'var(--hover-bg-color)' : 'transparent'};
	}
`

const ChevronIcon = styled.div`
	display: flex;
	align-items: center;
	color: var(--sub-text-color);
	font-size: 14px;
`

export default BookStatisticsCategoryCard
